import { useEffect, useMemo } from 'react';
import { Box, Button, EmptyState, SkeletTable } from '@alf/uikit';
import { useI18n } from '@namespace/i18n';
import {
  notificationCenterActions,
  useNotificationCenterStore
} from '../../../../store';
import { prepareFilterForRequest } from '../../../../utils/prepareFilterForRequest';
import { MessageItem } from './MessageItem';

import styles from './index.module.css';

const { FETCH_MESSAGES_LIST_DATA, LOAD_MORE_MESSAGES } =
  notificationCenterActions;

export const NotificationMessagesList = ({ currentFilter }) => {
  const {
    messagesListPaginationData,
    messages,
    isFetchingMessagesList,
    isFetchingMoreMessages
  } = useNotificationCenterStore([
    'messagesListPaginationData',
    'messages',
    'isFetchingMessagesList',
    'isFetchingMoreMessages'
  ]);

  const { offset, initialLimit, totalCount } = messagesListPaginationData;
  const { ids: messagesIds, data: messagesData } = messages;
  const { t, f } = useI18n();

  const emptyStateText = useMemo(() => {
    const { dateOptionId, status, category } = currentFilter;

    if (dateOptionId === '90' && status === 'all' && category === 'all') {
      return {
        text: t('web.personaloffice.messages.nomessages.1'),
        subText: t('web.personaloffice.messages.nomessages.2')
      };
    }

    return {
      text: t('web.personaloffice.messages.noresultsforfilters.1'),
      subText: t('web.personaloffice.messages.noresultsforfilters.2')
    };
  }, [currentFilter, t]);

  const loadMore = async () => {
    await LOAD_MORE_MESSAGES({
      filter: prepareFilterForRequest(currentFilter, f),
      pagination: {
        limit: initialLimit,
        offset
      }
    });
  };

  useEffect(() => {
    (async () => {
      await FETCH_MESSAGES_LIST_DATA({
        filter: prepareFilterForRequest(currentFilter, f),
        pagination: {
          limit: initialLimit,
          offset: 0
        }
      });
    })();
  }, []);

  if (!isFetchingMessagesList && !messagesIds.length) {
    return (
      <EmptyState
        text={emptyStateText.text}
        subText={emptyStateText.subText}
        className={styles.emptyStateContainer}
        classNames={{
          textClassName: styles.emptyStateText,
          subTextClassName: styles.emptyStateSubText
        }}
      />
    );
  }

  return (
    <>
      {isFetchingMessagesList ? (
        <SkeletTable
          classNames={{ classNameItem: styles.skeletItem }}
          skeletItems={5}
        />
      ) : (
        <Box
          direction="column"
          align="center"
          justify="center"
          className={styles.container}
        >
          {messagesIds.map((messageId) => (
            <div key={messageId} className={styles.messageItemContainer}>
              <MessageItem data={messagesData[messageId]} />
            </div>
          ))}
          {offset < totalCount && (
            <Button
              onClick={loadMore}
              size="m"
              actionType="color3"
              intent="quaternary"
              processing={isFetchingMoreMessages}
            >
              {t('web.personaloffice.messages.showmore.btn')}
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
